import { useState } from "react";

import { Grid2, IconButton, Typography } from "@mui/material";
import moment from "moment";
import { FaChevronLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

import { formatTimeRange } from "../../../../../utils";
import { TLocation } from "../FlightLogs";
import { EditFlightDetail } from "./EditFlightDetail";
import {
	TFlightLog,
	TFlightLogComment,
	THeartRate,
	TProfile,
} from "../../../../../@types";
import { useAccount } from "../../../../../hooks/useAccount";
import { FlightStorySections } from "./FlightStorySections";

export type TFlightDetailProps = {
	flightLog: TFlightLog;
	gpsData: TLocation[];
	landing: number | null;
	distance: number | null;
	heartRateData?: THeartRate[];
	comments?: TFlightLogComment[];
	addComment?: (content: string) => Promise<void>;
	deleteComment?: (commentId: number) => Promise<void>;
	updateFlightLogData: (id: number, flightLog: TFlightLog) => void;
};

function getProfileName(profile?: TProfile) {
	const firstName = profile?.user?.firstName ?? "";
	const lastName = profile?.user?.lastName ?? "";
	return `${firstName} ${lastName}`.trim();
}

function getCopilotNames(flightLog: TFlightLog) {
	if (!flightLog.coPilots) {
		return [];
	}
	try {
		const copilots = JSON.parse(flightLog.coPilots) as { profile?: TProfile }[];
		return copilots
			.filter(({ profile }) => profile?.user?.email !== flightLog.email)
			.map(({ profile }) => getProfileName(profile))
			.filter(Boolean);
	} catch {
		return [];
	}
}

export function FlightDetail(props: TFlightDetailProps) {
	const navigate = useNavigate();
	const { account } = useAccount();
	type TState = {
		isEditing: boolean;
	};
	const [state, setState] = useState<TState>({
		isEditing: false,
	});

	const timezoneName = props.flightLog.timezoneName || moment.tz.guess();
	const localStart = moment(Number(props.flightLog.startTime)).tz(timezoneName);
	const localEnd = moment(Number(props.flightLog.endTime)).tz(timezoneName);
	const { dateText, timeText } = formatTimeRange(localStart, localEnd);
	const zuluTimeText = `${moment(Number(props.flightLog.startTime)).utc().format("HH:mm")} - ${moment(Number(props.flightLog.endTime)).utc().format("HH:mm")}Z`;
	const ownerName = getProfileName(props.flightLog.profile) || "FlyORKA pilot";
	const copilotNames = getCopilotNames(props.flightLog);
	const aircraft = [props.flightLog.makeAndModel, props.flightLog.callSigns]
		.filter(Boolean)
		.join(" · ");
	const duration =
		Number(props.flightLog.endTime) - Number(props.flightLog.startTime);
	const hours = Math.floor(duration / (60 * 60 * 1000));
	const minutes = Math.round((duration - hours * 60 * 60 * 1000) / (60 * 1000));
	let durationText = "";
	if (hours === 1) {
		durationText += `${hours} hour`;
	} else if (hours > 1) {
		durationText += `${hours} hours`;
	}
	if (minutes === 1) {
		durationText += ` ${minutes} minute`;
	} else if (minutes > 1) {
		durationText += ` ${minutes} minutes`;
	}

	durationText = durationText.trim();
	if (durationText.length === 0) {
		durationText = "0 minutes";
	}

	if (state.isEditing) {
		return (
			<EditFlightDetail
				cancelEdit={() => {
					setState((prevState) => ({
						...prevState,
						isEditing: false,
					}));
				}}
				flightLog={props.flightLog}
				updateFlightLogData={props.updateFlightLogData}
			/>
		);
	}

	return (
		<Grid2
			size={{ xs: 12, sm: 12, md: 3, lg: 3, xl: 3 }}
			className="flight-log-detail">
			<div className="flight-log-detail-header">
				<IconButton
					onClick={() => {
						navigate("/flight-logs");
					}}>
					<FaChevronLeft className="normal-icon" />
				</IconButton>
				<div className="flight-story-heading">
					<Typography className="normal-text bold">{ownerName}</Typography>
					<Typography className="small-text">{`${dateText}, ${timeText}`}</Typography>
				</div>
			</div>
			<div className="flight-log-detail-content">
				<div className="flight-story-timezone-row">
					<span>{timezoneName}</span>
					<span>{zuluTimeText}</span>
				</div>
				<Grid2 container>
					<Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
						<Typography className="normal-text bold">Start</Typography>
					</Grid2>
					<Grid2 size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
						<Typography className="normal-text">
							{localStart.format("HH:mm:ss, DD MMM YYYY")}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
						<Typography className="normal-text bold">End</Typography>
					</Grid2>
					<Grid2 size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
						<Typography className="normal-text">
							{localEnd.format("HH:mm:ss, DD MMM YYYY")}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
						<Typography className="normal-text bold">Flight time</Typography>
					</Grid2>
					<Grid2 size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
						<Typography className="normal-text">{durationText}</Typography>
					</Grid2>
					{props.flightLog.airport && (
						<>
							<Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
								<Typography className="normal-text bold">Airport</Typography>
							</Grid2>
							<Grid2 size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
								<Typography className="normal-text">
									{props.flightLog.airport}
								</Typography>
							</Grid2>
						</>
					)}
					{aircraft && (
						<>
							<Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
								<Typography className="normal-text bold">Aircraft</Typography>
							</Grid2>
							<Grid2 size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
								<Typography className="normal-text">{aircraft}</Typography>
							</Grid2>
						</>
					)}
					{copilotNames.length > 0 && (
						<>
							<Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
								<Typography className="normal-text bold">Copilots</Typography>
							</Grid2>
							<Grid2 size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
								<Typography className="normal-text">
									{copilotNames.join(", ")}
								</Typography>
							</Grid2>
						</>
					)}
					<div className="separator" />
					<Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<div className="flight-takeaways-header-container">
							<Typography className="normal-text bold">
								Flight Takeaways
							</Typography>

							{account?.email === props.flightLog.email && (
								<Typography
									className="normal-text edit-button"
									onClick={() => {
										setState((prevState) => ({
											...prevState,
											isEditing: true,
										}));
									}}>
									Edit
								</Typography>
							)}
						</div>
						{account?.email === props.flightLog.email && (
							<Typography whiteSpace="break-spaces" className="normal-text">
								{props.flightLog.description ||
									"How do you know what to do, if you don't remember what you did? Write takeaways."}
							</Typography>
						)}
						{account?.email !== props.flightLog.email && (
							<Typography whiteSpace="break-spaces" className="normal-text">
								{props.flightLog.description ||
									"User has not added any takeaways."}
							</Typography>
						)}
						{props.gpsData.length > 0 && (
							<div className="flight-takeaways-analytics-container">
								<div className="flight-takeaways-analytics-item">
									<Typography className="big-text bold">
										{`${(duration / (60 * 60 * 1000)).toFixed(1)} hrs`}
									</Typography>
									<Typography className="normal-text">In Flight</Typography>
								</div>
								<div className="vertical-line" />
								<div className="flight-takeaways-analytics-item">
									<Typography className="big-text bold">
										{`${((props.distance ?? 0) * 0.621371).toFixed(1)} mi`}
									</Typography>
									<Typography className="normal-text">Flown</Typography>
								</div>
								<div className="vertical-line" />
								<div className="flight-takeaways-analytics-item">
									<Typography className="big-text bold">
										{props.flightLog.landing || props.landing || 0}
									</Typography>
									<Typography className="normal-text">Landings</Typography>
								</div>
							</div>
						)}
					</Grid2>
				</Grid2>
				<FlightStorySections
					gpsData={props.gpsData}
					heartRateData={props.heartRateData ?? []}
					comments={props.comments ?? []}
					addComment={props.addComment ?? (async () => undefined)}
					deleteComment={props.deleteComment ?? (async () => undefined)}
				/>
			</div>
		</Grid2>
	);
}
